import React, { useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, LayoutDashboard, LogOut } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { authApi, clearAuth, getCachedUser, getHasActivePlan, getDashboardPathForUser } from '../lib/api';

type HeaderVariant = 'default' | 'logoOnly';

const Header: React.FC<{ variant?: HeaderVariant }> = ({ variant = 'default' }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Normalize pathname + casse (/Partners/…, /partners/dashboard) pour landing, sign-out, lien Dashboard
  const rawPath = location.pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  const pathname = (rawPath.replace(/\/$/, '') || '/');
  const pathLower = pathname.toLowerCase();
  const isFR = pathLower.startsWith('/fr');
  const isPartners = pathLower.startsWith('/partners');
  const isUsHome = !isFR && !isPartners && pathLower === '/';
  const isFRPartenaires = pathLower.startsWith('/fr/partenaires');
  const basePathFR = isFR ? (isFRPartenaires ? '/fr/partenaires' : '/fr') : '';
  const basePathEN = !isFR && isPartners ? '/partners' : '';
  const effectiveUserType = user?.user_type ?? getCachedUser()?.user_type;
  const isPartnerAccount = effectiveUserType === 'partner';
  const homePath = isFR
    ? (isPartnerAccount ? '/fr/partenaires' : basePathFR)
    : isPartnerAccount
      ? '/partners'
      : basePathEN || '/';
  /** Sur /partners/* ou /fr/partenaires/* : toujours le TB photographes (évite /dashboard US). Ailleurs : compte + URL. */
  const dashboardPath = isFRPartenaires
    ? '/fr/partenaires/dashboard'
    : isPartners
      ? '/partners/dashboard'
      : getDashboardPathForUser(
          effectiveUserType === 'partner' || effectiveUserType === 'agent'
            ? { user_type: effectiveUserType }
            : user ?? getCachedUser(),
          pathname
        );
  const isOnboardingPage = pathLower.endsWith('/onboarding/plan');
  const showDashboardButton = !!user && !isOnboardingPage && getHasActivePlan() && pathLower !== '/dashboard' && pathLower !== '/partners/dashboard' && pathLower !== '/fr/dashboard' && pathLower !== '/fr/partenaires/dashboard';

  /** Navbar partenaires US : lien canonique absolu (évite SPA / mauvaise résolution de route). */
  const partnersUsDashboardAbsoluteUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/partners/dashboard`
      : 'https://3dtovideo.com/partners/dashboard';
  const partnersUsSignInAbsoluteUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/partners/signin`
      : 'https://3dtovideo.com/partners/signin';
  const partnersUsSignUpAbsoluteUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/partners/signup`
      : 'https://3dtovideo.com/partners/signup';

  const signInPath = isFR ? `${basePathFR}/signin` : (isPartners ? '/partners/signin' : '/signin');
  const signUpPath = isFR ? `${basePathFR}/signup` : (isPartners ? '/partners/signup' : '/signup');

  const handleSignOut = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await authApi.logout();
    } catch {
      clearAuth();
    }
    const pl = pathname.toLowerCase().replace(/\/$/, '') || '/';
    const onHome = pl === '/' || pl === '/partners' || pl === '/fr' || pl === '/fr/partenaires';
    if (onHome) {
      window.location.reload();
    } else {
      window.location.href = homePath;
    }
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setIsMenuOpen(false);

    if (variant === 'logoOnly') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (pathname !== homePath) {
      navigate(homePath);
    }

    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const headerHeight = 88;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - headerHeight,
        behavior: 'smooth'
      });
    }
    setIsMenuOpen(false);
  };

  return (
    <motion.header
      className="font-manrope sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 transition-all duration-300"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative flex justify-between items-center h-20 py-2">
          <div className="flex items-center gap-3 sm:gap-4 font-manrope">
            <Link to={homePath} onClick={handleLogoClick} className="flex items-center">
              <img 
                src="/logo3D.png" 
                alt="3DtoVideo Logo" 
                className="h-10 sm:h-11 w-auto max-w-[280px] cursor-pointer hover:opacity-90 transition-opacity"
              />
            </Link>
          </div>

          {/* Centered CTA (desktop): centered between logo and nav */}
          {isUsHome && null}

          {variant === 'logoOnly' ? null : (
          <nav className="hidden md:flex items-center space-x-8">
            {isFR ? (
              <>
                <a href={`${basePathFR}#pricing`} onClick={(e) => scrollToSection(e, 'pricing')} className="text-gray-600 hover:text-gray-900 transition-colors">Tarifs</a>
                {isFRPartenaires && (
                  <a href={`${basePathFR}#roi-calculator`} onClick={(e) => scrollToSection(e, 'roi-calculator')} className="text-gray-600 hover:text-gray-900 transition-colors">Calculateur ROI</a>
                )}
                <a href={`${basePathFR}#faq`} onClick={(e) => scrollToSection(e, 'faq')} className="text-gray-600 hover:text-gray-900 transition-colors">FAQ</a>
                <div className="flex items-center space-x-4 ml-2">
                  {user ? (
                    <>
                      {showDashboardButton && (
                        <Link to={dashboardPath} className="flex items-center gap-2 text-[#3C59B2] hover:text-[#2d4a8f] transition-colors font-medium">
                          <LayoutDashboard className="w-4 h-4" />
                          {isFR ? 'Tableau de bord' : 'Dashboard'}
                        </Link>
                      )}
                      <button type="button" onClick={handleSignOut} disabled={isLoggingOut} className="flex items-center gap-2 text-white bg-gray-900 hover:bg-gray-800 font-medium transition-colors px-3 py-2 rounded-full">
                        <LogOut className="w-4 h-4" />
                        {isLoggingOut ? (isFR ? 'Déconnexion…' : 'Signing out…') : (isFR ? 'Déconnexion' : 'Sign Out')}
                      </button>
                    </>
                  ) : (
                    <>
                      <a href={`${basePathFR}/signin`} className="text-gray-600 hover:text-[#3C59B2] font-medium transition-colors px-3 py-2 rounded-full">Connexion</a>
                      <a href={`${basePathFR}/signup`} className="btn-premium text-white px-6 py-2.5 rounded-full overflow-hidden font-medium transition-all bg-[#3C59B2] hover:bg-[#2d4a8f] flex items-center justify-center">S&apos;inscrire</a>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                {isUsHome && (
                  <a
                    href="/partners"
                    onClick={() => setIsMenuOpen(false)}
                    className="btn-premium text-white px-4 py-2 rounded-full overflow-hidden font-bold transition-all bg-[#3C59B2] hover:bg-[#2d4a8f] flex items-center justify-center"
                  >
                    For Photographers
                  </a>
                )}
                {isPartners && (
                  <a href={`${basePathEN}#roi-calculator`} onClick={(e) => scrollToSection(e, 'roi-calculator')} className="text-gray-600 hover:text-gray-900 transition-colors">ROI Calculator</a>
                )}
                <a href={`${basePathEN}#pricing`} onClick={(e) => scrollToSection(e, 'pricing')} className="text-gray-600 hover:text-gray-900 transition-colors">Pricing</a>
                <a href={`${basePathEN}#faq`} onClick={(e) => scrollToSection(e, 'faq')} className="text-gray-600 hover:text-gray-900 transition-colors">FAQ</a>
                <div className="flex items-center space-x-4 ml-2">
                  {user ? (
                    <>
                      {showDashboardButton && (
                        isPartners ? (
                          <a
                            href={partnersUsDashboardAbsoluteUrl}
                            className="flex items-center gap-2 text-[#3C59B2] hover:text-[#2d4a8f] transition-colors font-medium"
                          >
                            <LayoutDashboard className="w-4 h-4" />
                            Dashboard
                          </a>
                        ) : (
                          <Link to={dashboardPath} className="flex items-center gap-2 text-[#3C59B2] hover:text-[#2d4a8f] transition-colors font-medium">
                            <LayoutDashboard className="w-4 h-4" />
                            Dashboard
                          </Link>
                        )
                      )}
                      <button type="button" onClick={handleSignOut} disabled={isLoggingOut} className="flex items-center gap-2 text-white bg-gray-900 hover:bg-gray-800 font-medium transition-colors px-3 py-2 rounded-full">
                        <LogOut className="w-4 h-4" />
                        {isLoggingOut ? 'Signing out…' : 'Sign Out'}
                      </button>
                    </>
                  ) : (
                    <>
                      <a
                        href={isPartners ? partnersUsSignInAbsoluteUrl : signInPath}
                        className="text-gray-600 hover:text-[#3C59B2] font-medium transition-colors px-3 py-2 rounded-full"
                      >
                        Sign In
                      </a>
                      <a
                        href={isPartners ? partnersUsSignUpAbsoluteUrl : signUpPath}
                        className="btn-premium text-white px-6 py-2.5 rounded-full overflow-hidden font-medium transition-all bg-[#3C59B2] hover:bg-[#2d4a8f] flex items-center justify-center"
                      >
                        Sign Up
                      </a>
                    </>
                  )}
                </div>
              </>
            )}
          </nav>
          )}

          {variant === 'logoOnly' ? null : (
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden p-2">
              {isMenuOpen ? <X className="w-6 h-6 text-gray-600" /> : <Menu className="w-6 h-6 text-gray-600" />}
            </button>
          )}
        </div>

        {variant !== 'logoOnly' && isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-4">
              {isFR ? (
                <>
                  <a href={`${basePathFR}#pricing`} onClick={(e) => scrollToSection(e, 'pricing')} className="text-gray-600 hover:text-gray-900 transition-colors px-2">Tarifs</a>
                  {isFRPartenaires && (
                    <a href={`${basePathFR}#roi-calculator`} onClick={(e) => scrollToSection(e, 'roi-calculator')} className="text-gray-600 hover:text-gray-900 transition-colors px-2">Calculateur ROI</a>
                  )}
                  <a href={`${basePathFR}#faq`} onClick={(e) => scrollToSection(e, 'faq')} className="text-gray-600 hover:text-gray-900 transition-colors px-2">FAQ</a>
                  <div className="border-t border-gray-100 pt-4 flex flex-col gap-3 px-2">
                    {user ? (
                      <>
                        {showDashboardButton && (
                          <Link to={dashboardPath} className="flex items-center justify-center gap-2 text-[#3C59B2] hover:text-[#2d4a8f] font-medium transition-colors w-full" onClick={() => setIsMenuOpen(false)}>
                            <LayoutDashboard className="w-4 h-4" />
                            {isFR ? 'Tableau de bord' : 'Dashboard'}
                          </Link>
                        )}
                        <button type="button" onClick={() => { setIsMenuOpen(false); handleSignOut(); }} disabled={isLoggingOut} className="flex items-center justify-center gap-2 text-white bg-gray-900 hover:bg-gray-800 font-medium py-2 px-3 rounded-full transition-colors w-full max-w-[140px] mx-auto">
                          <LogOut className="w-4 h-4" />
                          {isLoggingOut ? (isFR ? 'Déconnexion…' : 'Signing out…') : (isFR ? 'Déconnexion' : 'Sign Out')}
                        </button>
                      </>
                    ) : (
                      <>
                        <a href={`${basePathFR}/signin`} className="text-gray-600 hover:text-[#3C59B2] font-medium text-center py-2 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors" onClick={() => setIsMenuOpen(false)}>Connexion</a>
                        <a href={`${basePathFR}/signup`} className="btn-premium text-white px-6 py-3 rounded-full overflow-hidden text-center font-medium bg-[#3C59B2] flex items-center justify-center" onClick={() => setIsMenuOpen(false)}>S&apos;inscrire</a>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {isUsHome && (
                    <a
                      href="/partners"
                      className="btn-premium text-white px-6 py-3 rounded-full overflow-hidden text-center font-bold bg-[#3C59B2] hover:bg-[#2d4a8f] flex items-center justify-center transition-colors mx-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      For Photographers
                    </a>
                  )}
                  {isPartners && (
                    <a href={`${basePathEN}#roi-calculator`} onClick={(e) => scrollToSection(e, 'roi-calculator')} className="text-gray-600 hover:text-gray-900 transition-colors px-2">ROI Calculator</a>
                  )}
                  <a href={`${basePathEN}#pricing`} onClick={(e) => scrollToSection(e, 'pricing')} className="text-gray-600 hover:text-gray-900 transition-colors px-2">Pricing</a>
                  <a href={`${basePathEN}#faq`} onClick={(e) => scrollToSection(e, 'faq')} className="text-gray-600 hover:text-gray-900 transition-colors px-2">FAQ</a>
                  <div className="border-t border-gray-100 pt-4 flex flex-col gap-3 px-2">
                    {user ? (
                      <>
                        {showDashboardButton && (
                          isPartners ? (
                            <a
                              href={partnersUsDashboardAbsoluteUrl}
                              className="flex items-center justify-center gap-2 text-[#3C59B2] hover:text-[#2d4a8f] font-medium transition-colors w-full"
                              onClick={() => setIsMenuOpen(false)}
                            >
                              <LayoutDashboard className="w-4 h-4" />
                              Dashboard
                            </a>
                          ) : (
                            <Link to={dashboardPath} className="flex items-center justify-center gap-2 text-[#3C59B2] hover:text-[#2d4a8f] font-medium transition-colors w-full" onClick={() => setIsMenuOpen(false)}>
                              <LayoutDashboard className="w-4 h-4" />
                              Dashboard
                            </Link>
                          )
                        )}
                        <button type="button" onClick={() => { setIsMenuOpen(false); handleSignOut(); }} disabled={isLoggingOut} className="flex items-center justify-center gap-2 text-white bg-gray-900 hover:bg-gray-800 font-medium py-2 px-3 rounded-full transition-colors w-full max-w-[120px] mx-auto">
                          <LogOut className="w-4 h-4" />
                          {isLoggingOut ? 'Signing out…' : 'Sign Out'}
                        </button>
                      </>
                    ) : (
                      <>
                        <a
                          href={isPartners ? partnersUsSignInAbsoluteUrl : signInPath}
                          className="text-gray-600 hover:text-[#3C59B2] font-medium text-center py-2 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          Sign In
                        </a>
                        <a
                          href={isPartners ? partnersUsSignUpAbsoluteUrl : signUpPath}
                          className="btn-premium text-white px-6 py-3 rounded-full overflow-hidden text-center font-medium bg-[#3C59B2] flex items-center justify-center"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          Sign Up
                        </a>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.header>
  );
};

export default Header;